import { z } from 'zod';

// ============================================
// PORTAL SETTINGS (M13-lite) — candidate account+privacy, company property+team.
// PDPL/security/sessions tabs are informational copy until the fine pass.
// ============================================

/** CAND_PRIVACY flags; absent key = default (everything on except hideFromFormerEmployers). */
export const CandidatePrivacySchema = z.object({
	showToPremium: z.boolean().default(true),
	allowInvitations: z.boolean().default(true),
	maskPhone: z.boolean().default(true),
	/**
	 * El email, tapado hasta la entrevista igual que el teléfono (12/09).
	 *
	 * La clienta pidió que el email se viera al desbloquear —hasta ahora no
	 * viajaba, pese a que la regla del 21/08 lo daba por incluido en lo que se
	 * paga— y eligió que el candidato pueda ocultarlo, como ya podía con el
	 * teléfono. Ausente = tapado, que es el valor prudente para los perfiles que
	 * nunca han tocado su privacidad.
	 */
	maskEmail: z.boolean().default(true),
	hideFromFormerEmployers: z.boolean().default(false),
	appearInSearch: z.boolean().default(true),
});

export const CandidateSettingsSchema = z.object({
	fullName: z.string(),
	phone: z.string(),
	email: z.string(),
	preferredLanguage: z.string().nullable(),
	privacy: CandidatePrivacySchema,
});

export const UpdateCandidateSettingsSchema = z.object({
	fullName: z.string().min(1).optional(),
	phone: z.string().min(1).optional(),
	privacy: CandidatePrivacySchema.partial().optional(),
});

export const CompanyTeamMemberSchema = z.object({
	/** Needed to revoke (03/09 · 26): the row used to carry no id at all. */
	userId: z.number().int(),
	name: z.string(),
	email: z.string(),
	memberRole: z.string(),
	/** The caller's own row: the screen never offers to revoke yourself. */
	isSelf: z.boolean(),
});

export const CompanySettingsSchema = z.object({
	companyName: z.string(),
	propertyType: z.string().nullable(),
	/** Ciudad de la propiedad (catálogo GCC_LOCATIONS). Ya vivía en `Company` y
	 *  el formulario no la ofrecía, así que solo se podía cambiar por soporte. */
	city: z.string().nullable(),
	numberOfRooms: z.number().nullable(),
	numberOfLocations: z.number().nullable(),
	linkedinUrl: z.string().nullable(),
	/** Logo o imagen de la propiedad, firmado (08/09). `null` = sin logo. */
	logoUrl: z.string().nullable(),
	team: z.array(CompanyTeamMemberSchema),
	/**
	 * Plazas de usuario del plan (E-P2-02): la pantalla las conoce ANTES de
	 * enviar la invitación, en vez de dejar que el backend rechace después de
	 * rellenar el formulario. `limit` null = sin tope.
	 */
	seats: z.object({
		used: z.number().int(),
		limit: z.number().int().nullable(),
	}),
});

export const UpdateCompanySettingsSchema = z.object({
	companyName: z.string().min(1).optional(),
	propertyType: z.string().nullish(),
	city: z.string().nullish(),
	numberOfRooms: z.number().int().min(1).nullish(),
	numberOfLocations: z.number().int().min(1).nullish(),
	linkedinUrl: z.string().nullish(),
});

/**
 * Payload for `POST /portal/company/settings/team` — invite a colleague into
 * the company account (M13 · invitar miembros).
 *
 * `owner` is deliberately not offerable: an invitation must not be able to
 * mint another account owner. Ownership changes are an admin operation.
 */
export const InviteCompanyMemberSchema = z.object({
	name: z.string().min(1),
	lastName: z.string().min(1),
	email: z.string().email(),
});

export type CandidatePrivacyDTO = z.infer<typeof CandidatePrivacySchema>;
export type CandidateSettingsDTO = z.infer<typeof CandidateSettingsSchema>;
export type UpdateCandidateSettingsDTO = z.infer<typeof UpdateCandidateSettingsSchema>;
export type CompanyTeamMemberDTO = z.infer<typeof CompanyTeamMemberSchema>;
export type CompanySettingsDTO = z.infer<typeof CompanySettingsSchema>;
export type UpdateCompanySettingsDTO = z.infer<typeof UpdateCompanySettingsSchema>;
export type InviteCompanyMemberDTO = z.infer<typeof InviteCompanyMemberSchema>;

/**
 * The person behind the account, for the "Mi cuenta" tab of both portals
 * (`GET /portal/me/account`).
 *
 * The email is read-only: it is the Supabase Auth identity and the login, so
 * changing it is a re-verification flow, not a text field. Same for the role,
 * which is shown so the user knows what they can do, never to be edited here.
 */
export const PortalAccountSchema = z.object({
	name: z.string(),
	lastName: z.string(),
	email: z.string(),
	phone: z.string().nullable(),
	/** 'company_owner' | 'company_member' | 'candidate' — label resolved client-side. */
	roleName: z.string(),
	/** Only for company accounts: which membership this user holds. */
	memberRole: z.string().nullable(),
});

/** Payload of `PUT /portal/me/account`. Only what the user may change. */
/**
 * Un teléfono: dígitos, con prefijo, espacios, paréntesis o guiones, y al
 * menos seis cifras (03/09 · 12: se guardaba «abc» y un nombre de espacios).
 */
export const PHONE_PATTERN = /^\+?[0-9][0-9 ()-]{4,38}[0-9]$/;
export const PhoneSchema = z.string().trim().regex(PHONE_PATTERN, 'INVALID_PHONE');

export const UpdatePortalAccountSchema = z.object({
	name: z.string().trim().min(1),
	lastName: z.string().trim().min(1),
	phone: z.preprocess(
		(value) => (typeof value === 'string' && value.trim() === '' ? null : value),
		PhoneSchema.nullish(),
	),
});

/**
 * Payload of `PUT /portal/me/language`.
 *
 * Its own route because the language switcher shipped in both portals writes
 * nothing else, and the staff route it used instead (`PUT /users/:id`) sits
 * behind `StaffOnlyGuard`: a portal account got a 403 on every switch and the
 * preference never left the browser.
 */
export const UpdatePortalLanguageSchema = z.object({
	preferredLanguage: z.string().min(2).max(8),
});

/**
 * Changing your own password from the portal (`PUT /portal/me/password`).
 *
 * `currentPassword` is REQUIRED here, unlike the admin route it replaces: this
 * one is reached with nothing but a session, so without it a stolen or
 * borrowed tab would be enough to lock the owner out of their own account.
 */
export const ChangeOwnPasswordSchema = z.object({
	currentPassword: z.string().min(1),
	newPassword: z.string().min(8),
});

export type PortalAccountDTO = z.infer<typeof PortalAccountSchema>;
export type UpdatePortalAccountDTO = z.infer<typeof UpdatePortalAccountSchema>;
export type UpdatePortalLanguageDTO = z.infer<typeof UpdatePortalLanguageSchema>;
export type ChangeOwnPasswordDTO = z.infer<typeof ChangeOwnPasswordSchema>;
